import React from 'react';
import useRedirectToValidFlowPath from 'src/viewLayer/components/hooks/flowHooks/useRedirectToValidFlowPath';
import useSetQueryParams from 'src/viewLayer/components/hooks/flowHooks/useSetQueryParams';
import usePerformanceTracker from 'src/viewLayer/components/hooks/flowHooks/usePerformanceTracker';
import useNodeTracker from 'src/viewLayer/components/hooks/flowHooks/useNodeTracker';
import usePushToDataLayer from 'src/viewLayer/components/hooks/flowHooks/usePushToDataLayer';
import { getValidatedQueryStringFromParams } from 'src/dataLayer/localData/queryParams';
import useInitGTM from 'src/viewLayer/components/hooks/flowHooks/useInitGTM';
import useABTester from 'src/viewLayer/components/hooks/flowHooks/useABTester';
import useFetchFlow from 'src/viewLayer/components/hooks/flowHooks/useFetchFlow';
import useGeoConfig from './useGeoConfig';
import type { FlowConfig, RouteConfigRef } from 'src/viewLayer/components/Flow/types';
import type { Navigate } from 'src/types/navigate';

interface UseFlowHooksParams {
  queryParams: URLSearchParams;
  setQueryParams: Navigate;
  learnLang: string;
  routeConfig: { current: RouteConfigRef };
  navigate: Navigate;
  flowConfig: FlowConfig;
  setFlowConfig: React.Dispatch<React.SetStateAction<FlowConfig>>;
  flowId: string;
  nodeNumber: number;
}

export const useFlowHooks = ({
  queryParams,
  setQueryParams,
  learnLang,
  routeConfig,
  navigate,
  flowConfig,
  setFlowConfig,
  flowId,
  nodeNumber
}: UseFlowHooksParams): void => {
  const flow = flowConfig?.flow;
  const { current } = routeConfig;
  const validatedQueryParams = getValidatedQueryStringFromParams(queryParams, learnLang);
  const isInvalidNodeNumber = flow?.length && !(nodeNumber < flow?.length);

  useSetQueryParams(validatedQueryParams, setQueryParams);
  useRedirectToValidFlowPath({ isInvalidNodeNumber, routeConfigRef: current, learnLang, navigate });
  useABTester({
    routeConfigRef: current,
    flowConfig,
    flowId,
    learnLang,
    navigate
  });
  useGeoConfig({
    routeConfigRef: current,
    flowId,
    learnLang,
    navigate
  });
  useFetchFlow({ flowConfig, flowId, setFlowConfig });
  useInitGTM(learnLang);
  usePerformanceTracker(learnLang);
  useNodeTracker({ learnLang, nodeNumber, flowConfig, isInvalidNodeNumber });
  usePushToDataLayer({ nodeNumber, flowConfig });
};
